import { createContext, useContext, useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { onAuthStateChanged, signInWithEmailAndPassword, signOut } from 'firebase/auth';
import { doc, onSnapshot } from 'firebase/firestore';
import { auth, db } from '../services/firebase';
import { getUserDoc } from '../services/userService';
import { identifyUser, resetUser } from '../services/monitoring';
import { clearAllCaches } from '../services/cacheService';

const AuthContext = createContext(null);

/**
 * AuthProvider — wraps the app once (in main.jsx) and holds a single
 * onAuthStateChanged listener + a realtime Firestore onSnapshot on the
 * user document. Any server-side change (class removal, role update, name
 * change) is reflected automatically without a page reload.
 */
export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [role, setRole] = useState(null);
  const [displayName, setDisplayName] = useState(null);
  const [userDoc, setUserDocState] = useState(null);
  const [loading, setLoading] = useState(true);
  const unsubSnapshotRef = useRef(null);

  useEffect(() => {
    let cancelled = false;

    function applyUserDoc(docData, uid) {
      setRole(docData.role || null);
      setDisplayName(docData.displayName || null);
      setUserDocState(docData);
      identifyUser(uid, {
        role: docData.role,
        classes: docData.classes || [],
        name: docData.displayName || null,
        phone: docData.phone || null,
      });
    }

    function teardownSnapshot() {
      if (unsubSnapshotRef.current) {
        unsubSnapshotRef.current();
        unsubSnapshotRef.current = null;
      }
    }

    const unsub = onAuthStateChanged(auth, async (u) => {
      teardownSnapshot();

      if (u) {
        if (u.isAnonymous) {
          if (!cancelled) setLoading(false);
          return;
        }
        setLoading(true);
        setUser(u);
        try {
          const initialDoc = await getUserDoc(u.uid);
          if (cancelled) return;
          if (!initialDoc) {
            await signOut(auth);
            return;
          }
          applyUserDoc(initialDoc, u.uid);
          if (!cancelled) setLoading(false);

          unsubSnapshotRef.current = onSnapshot(
            doc(db, 'users', u.uid),
            (snap) => {
              if (cancelled || !snap.exists()) return;
              const liveDoc = { id: snap.id, ...snap.data() };
              applyUserDoc(liveDoc, u.uid);
            },
            (err) => {
              console.error('userDoc snapshot error:', err);
            },
          );
        } catch (err) {
          if (cancelled) return;
          console.error('Failed to fetch user role:', err);
          setUser(null);
          setRole(null);
          setDisplayName(null);
          setUserDocState(null);
          signOut(auth).catch(() => {});
          if (!cancelled) setLoading(false);
        }
      } else {
        setUser(null);
        setRole(null);
        setDisplayName(null);
        setUserDocState(null);
        resetUser();
        clearAllCaches();
        if (!cancelled) setLoading(false);
      }
    });
    return () => { cancelled = true; teardownSnapshot(); unsub(); };
  }, []);

  const login = useCallback(
    (email, password) => signInWithEmailAndPassword(auth, email, password),
    [],
  );
  const logout = useCallback(() => signOut(auth), []);

  const refreshRole = useCallback(async () => {
    if (!user) return;
    try {
      const freshDoc = await getUserDoc(user.uid);
      setRole(freshDoc?.role || null);
      setDisplayName(freshDoc?.displayName || null);
      setUserDocState(freshDoc || null);
    } catch (err) {
      console.error('refreshRole failed, keeping current role:', err);
    }
  }, [user]);

  const value = useMemo(
    () => ({ user, role, displayName, userDoc, loading, login, logout, refreshRole }),
    [user, role, displayName, userDoc, loading, login, logout, refreshRole],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

/**
 * useAuth — reads auth state from the nearest AuthProvider.
 * Must be used inside <AuthProvider>; throws if used outside.
 */
export function useAuth() {
  const ctx = useContext(AuthContext);
  if (ctx === null) {
    throw new Error('useAuth must be used inside <AuthProvider>');
  }
  return ctx;
}
